import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlus, FiArrowLeft, FiEdit2, FiTrash2, FiTool } from "react-icons/fi";
import { getHostelById, getRooms, deleteRoom } from "../../services/hostelService";

const DEFAULT_ROOM_RENTS = {
    Single: 4000,
    Double: 3000,
    Triple: 1500,
    Dormitory: 1500,
};

const getRent = (room) => room.roomRent !== undefined && room.roomRent !== null && room.roomRent > 0 ? room.roomRent : (DEFAULT_ROOM_RENTS[room.roomType] || 3000);

const statusClass = {
    Available: "badge-available",
    Full: "badge-full",
    Maintenance: "badge-maintenance",
};

export default function RoomList() {
    const { hostelId } = useParams();
    const navigate = useNavigate();

    const [hostel, setHostel] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadData = async () => {
        setLoading(true);
        try {
            const [hostelRes, roomsRes] = await Promise.all([
                getHostelById(hostelId),
                getRooms(hostelId),
            ]);
            setHostel(hostelRes.data);
            setRooms(roomsRes.data || []);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to load rooms");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [hostelId]); // eslint-disable-line

    const handleDelete = async (room) => {
        if (
            !window.confirm(
                `Delete Room ${room.roomNumber}? Only possible if it has no active occupants.`,
            )
        )
            return;
        try {
            await deleteRoom(room._id);
            toast.success("Room deleted");
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to delete room");
        }
    };

    if (loading) {
        return (
            <div className="card">
                <div className="loading-center">
                    <div className="spinner" />
                    Loading rooms...
                </div>
            </div>
        );
    }

    const totalCapacity = rooms.reduce((s, r) => s + r.capacity, 0);
    const totalOccupied = rooms.reduce((s, r) => s + r.occupiedBeds, 0);

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">{hostel?.name} — Rooms</h1>
                    <p className="page-subtitle">{rooms.length} room(s)</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate("/hostels")}>
                        <FiArrowLeft /> Back
                    </button>
                    <Link to={`/hostels/${hostelId}/rooms/new`} className="btn btn-primary">
                        <FiPlus /> Add Room
                    </Link>
                </div>
            </div>

            <div className="stat-strip">
                <div className="stat-chip">
                    <span className="stat-label">Total Rooms</span>
                    <span className="stat-value">{rooms.length}</span>
                </div>
                <div className="stat-chip">
                    <span className="stat-label">Total Capacity</span>
                    <span className="stat-value">{totalCapacity}</span>
                </div>
                <div className="stat-chip">
                    <span className="stat-label">Occupied</span>
                    <span className="stat-value">{totalOccupied}</span>
                </div>
                <div className="stat-chip">
                    <span className="stat-label">Vacant</span>
                    <span className="stat-value">{totalCapacity - totalOccupied}</span>
                </div>
            </div>

            {rooms.length === 0 ? (
                <div className="card">
                    <div className="empty-state">
                        <p>No rooms added yet for this hostel.</p>
                        <Link to={`/hostels/${hostelId}/rooms/new`} className="btn btn-primary">
                            <FiPlus /> Add first room
                        </Link>
                    </div>
                </div>
            ) : (
                <div className="room-grid">
                    {rooms.map((room) => (
                        <div key={room._id} className={`room-tile ${statusClass[room.status]}`}>
                            <div className="room-tile-header">
                                <strong>Room {room.roomNumber}</strong>
                                <span className={`badge ${statusClass[room.status]}`}>
                                    {room.status === "Maintenance" && <FiTool />} {room.status}
                                </span>
                            </div>
                            <p className="room-tile-meta">
                                {room.roomType} {room.floor ? `· Floor ${room.floor}` : ""} · <strong style={{ color: "#16a34a" }}>₹{getRent(room).toLocaleString("en-IN")}/mo</strong>
                            </p>
                            <p className="room-tile-occupancy">
                                {room.occupiedBeds} / {room.capacity} beds filled
                            </p>
                            <div className="room-tile-actions">
                                <button
                                    className="btn btn-icon"
                                    onClick={() => navigate(`/hostels/${hostelId}/rooms/${room._id}/edit`)}
                                    title="Edit"
                                >
                                    <FiEdit2 />
                                </button>
                                <button
                                    className="btn btn-icon btn-danger"
                                    onClick={() => handleDelete(room)}
                                    title="Delete"
                                    disabled={room.occupiedBeds > 0}
                                >
                                    <FiTrash2 />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}